<template>
	<div class="theme-landing">
		<SearchBarCore
			v-if="isDesktop"
			class="theme-landing-form"
			@search-submit="onSubmit"
		/>
		<!-- mobile searchmask -->
		<form
			v-if="!isDesktop"
			class="theme-landing-form"
			autocomplete="off"
			@submit.prevent="onSubmit"
		>
			<Collapse
				label="Suche"
				class="theme-landing-form__collapse"
				:block="true"
				:open="true"
			>
				<SearchBarCore
					:variant="'inline'"
					class="theme-landing-form__search-bar--mobile"
					:show-submit-button="false"
				/>
			</Collapse>

			<!-- mobile filters -->
			<Collapse
				:label="'Filter ' + activeFilterHeadline"
				class="theme-landing-form__collapse"
				:block="true"
				:open="true"
			>
				<FilterTagList
					class="theme-landing-form__filter-tag-list"
					:show-reset-in-header="true"
				/>
				<ExpandTransition>
					<div
						v-if="!isHotelOnly"
						class="theme-landing-form__transfer-group"
					>
						<h2 class="theme-landing-form__subheader--hotellist">Flug und Transport</h2>
						<DirectFlight class="theme-landing-form__direct-flight-field" />
						<TransferField class="theme-landing-form__transfer-field" />
					</div>
				</ExpandTransition>
				<div class="theme-landing-form__stay-group">
					<h2 class="theme-landing-form__subheader--hotellist">Verpflegung und Zimmer</h2>
					<BoardField class="theme-landing-form__board-types-field" />
					<RoomTypeField class="theme-landing-form__room-type-field" />
					<SeaView class="theme-landing-form__sea-view-field" />
				</div>

				<h2
					class="theme-landing-form__subheader--hotellist hotel-list-filters__header--before-input hotel-list-filters__header--hotel-category"
				>
					Hotelkategorie ab
				</h2>
				<HotelCategory
					:value="formData.hotelCategory"
					:show-suns="showSuns"
					class="theme-landing-form__hotel-category-filter"
				/>
				<h2 class="theme-landing-form__subheader--hotellist hotel-list-filters__header--before-input">Weiterempfehlungsrate ab</h2>
				<RecommendationRate
					:value="formData.minMeanRecommendationRate"
					class="theme-landing-form__recommendation-filter"
				/>
				<PriceSlider
					:default-value="formData.maxPrice"
					:currency="currencyType"
					class="theme-landing-form__price-filter"
				/>

				<div class="theme-landing-form__additional-group">
					<Collapse
						collapse-headline="Beliebteste Filter"
						class="theme-landing-form__collapse hotel-list-filters__collapse hotel-list-filters__most-popular"
						:open="true"
					>
						<template #header>
							<h2 class="theme-landing-form__subheader--hotellist hotel-list-filters__header--before-input">
								Beliebteste Filter
							</h2>
						</template>
						<MostPopularFilters :filters="formData.mostPopularFilters" />
					</Collapse>
					<Collapse
						class="theme-landing-form__collapse hotel-list-filters__collapse"
						:open="openAdditionalCheckboxFilters"
					>
						<template #header>
							<h2 class="theme-landing-form__subheader--hotellist hotel-list-filters__header--before-input">Mehr Filter</h2>
						</template>
						<AdditionalCheckboxFilters :filters="formData.additionalCheckboxFilters" />
					</Collapse>
					<OperatorFilter
						v-if="!tourOperatorLoading"
						class="theme-landing-form__operator-filter"
						:disabled="!operatorTypes || operatorTypes.length == 0"
					/>
					<SkeletonLoader
						v-else
						class="hotel-list-filters__skeleton"
						:loader-width="100"
						:loader-height="7"
						:border-radius="'1rem'"
						:width-unit="'%'"
					/>
				</div>
			</Collapse>
			<div class="theme-landing-form__footer">
				<RoundedButton
					type="submit"
					theme="extra"
					class="theme-landing-form__submit-button"
					:disabled="maxTravelDurationError"
					@keydown.enter.prevent="onSubmit"
					@keydown.space.prevent="onSubmit"
				>
					<span class="theme-landing-form__submit-button-text"> Suchen </span>
				</RoundedButton>
			</div>
		</form>

		<!--Desktop View Filters -->
		<section
			v-if="isDesktop"
			class="theme-landing-filter-section"
		>
			<FilterTagList
				:show-reset-in-header="false"
				class="theme-landing-filter-section__filter-list"
			/>
			<div class="theme-landing-filter-section__line" />
			<div class="theme-landing-filter-section__item">
				<h2
					class="theme-landing-filter-section__subheader"
					:class="isHotelOnly ? 'theme-landing-filter-section__subheader--disabled' : ''"
				>
					Transfer und Flug
				</h2>
				<TransferField
					:disabled="isHotelOnly"
					class="theme-landing-filter-section__dropdown"
				/>
				<DirectFlight :disabled="isHotelOnly" />
			</div>

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Zimmer</h2>
				<RoomTypeField class="theme-landing-filter-section__dropdown" />
			</div>

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Verpflegung</h2>
				<BoardField class="theme-landing-filter-section__dropdown" />
				<SeaView />
			</div>

			<div class="theme-landing-filter-section__line" />

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Weiterempfehlungsrate ab</h2>
				<RecommendationRate :value="formData.minMeanRecommendationRate" />
			</div>

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Hotelkategorie ab</h2>
				<HotelCategory
					:value="formData.hotelCategory"
					:show-suns="showSuns"
				/>
			</div>

			<div class="theme-landing-filter-section__item">
				<PriceSlider
					:default-value="formData.maxPrice"
					:currency="currencyType"
				/>
			</div>
			<div class="theme-landing-filter-section__line" />
			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Beliebteste Filter</h2>
				<MostPopularFilters
					class="theme-landing-filter-section__checkbox-group"
					:filters="formData.mostPopularFilters"
				/>
			</div>

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Mehr Filter</h2>
				<AdditionalCheckboxFilters
					class="theme-landing-filter-section__checkbox-group"
					:filters="formData.additionalCheckboxFilters"
				/>
			</div>

			<div class="theme-landing-filter-section__item">
				<h2 class="theme-landing-filter-section__subheader">Reiseveranstalter</h2>
				<OperatorFilter
					v-if="!tourOperatorLoading"
					class="theme-landing-form__operator-filter"
					:disabled="!operatorTypes || operatorTypes.length == 0"
				/>
				<SkeletonLoader
					v-else
					class="hotel-list-filters__skeleton"
					:loader-width="100"
					:loader-height="7"
					:border-radius="'1rem'"
					:width-unit="'%'"
				/>
			</div>
			<RoundedButton
				type="submit"
				theme="extra"
				class="theme-landing-filter-section__submit-button"
				:disabled="maxTravelDurationError"
				@keydown.enter.prevent="onSubmit"
				@keydown.space.prevent="onSubmit"
				@click.prevent="onSubmit"
			>
				<span class="theme-landing-form__submit-button-text">Suchen</span>
			</RoundedButton>
		</section>
	</div>
</template>

<script lang="ts" setup>
import type { SearchFormDataType } from '@interfaces/search-form';
import BoardField from '@lmt-rpb/BoardField/BoardField.vue';
import Collapse from '@lmt-rpb/Collapse/Collapse.vue';
import DirectFlight from '@lmt-rpb/DirectFlight/DirectFlight.vue';
import HotelCategory from '@lmt-rpb/HotelCategory/HotelCategory.vue';
import AdditionalCheckboxFilters from '@lmt-rpb/AdditionalCheckboxFilters/AdditionalCheckboxFilters.vue';
import MostPopularFilters from '@lmt-rpb/MostPopularFilters/MostPopularFilters.vue';
import OperatorFilter from '@lmt-rpb/OperatorFilter/OperatorFilter.vue';
import PriceSlider from '@lmt-rpb/PriceSlider/PriceSlider.vue';
import RecommendationRate from '@lmt-rpb/RecommendationRate/RecommendationRate.vue';
import RoomTypeField from '@lmt-rpb/RoomTypeField/RoomTypeField.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import SeaView from '@lmt-rpb/SeaView/SeaView.vue';
import TransferField from '@lmt-rpb/TransferField/TransferField.vue';
import SkeletonLoader from '@lmt-rpb/SkeletonLoader/SkeletonLoader.vue';
import FilterTagList from '@lmt-rpb/FilterTagList/FilterTagList.vue';
import { ref, computed } from 'vue';
import SearchBarCore from '@lmt-rpb/Organisms/SearchBarCore/SearchBarCore.vue';
import ExpandTransition from '@lmt-rpb/Atoms/ExpandTransition/ExpandTransition.vue';
import type { OperatorTypesType } from '@/components/common/types';
import { useStore } from '@/components/common/store';
import { isClientGermany } from '@/js/utils/environmentUtils';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import { useFilters } from 'src/composables/useFIlters';

const store = useStore();

const formData = computed((): SearchFormDataType => store.state.searchMask);
const emit = defineEmits(['ThemeLandingPage:Submit']);
const isInitialLoad = ref(true);
const isHotelOnly = computed((): boolean => formData.value.onlyHotel);
const { isDesktop } = storeToRefs(useBreakpointStore());
const operatorTypes = computed((): OperatorTypesType[] => store.state.types.operator);
const { filtersCount } = useFilters();

const activeFilterHeadline = computed((): string => (filtersCount.value ? `(${filtersCount.value} aktiv)` : ''));
const showSuns = isClientGermany();
const currencyType = computed(() => store.getters.getCurrencyType);

const maxTravelDurationError = computed(() => store.state.searchMask.maxTravelDurationError);

const tourOperatorLoading = computed((): boolean => store.state.tourOperatorLoading);

const isOpenAdditionalCheckboxFilters = (): boolean => {
	if (!isInitialLoad.value) {
		return true;
	}

	if (
		formData.value.additionalCheckboxFilters?.ratingAttributes?.length ||
		formData.value.additionalCheckboxFilters?.hotelAttributes?.length
	) {
		isInitialLoad.value = false;
		return true;
	}

	return false;
};

const openAdditionalCheckboxFilters = computed((): boolean => isOpenAdditionalCheckboxFilters());
const onSubmit = (): void => {
	emit('ThemeLandingPage:Submit');
};
</script>

<style lang="scss" scoped>
// stylelint-disable
:deep(.collapse__icon) {
	width: 3rem;
	height: 2.3rem;
	fill: $color-primary;
	align-self: center;
}
// stylelint-enable
:deep(.collapse__header.is-block) {
	background-color: $color-white;
	box-shadow:
		-1.5rem 0 0 $color-white,
		1.5rem 0 0 $color-white;
	padding: 0.6rem 0;
}

:deep(.theme-landing-form__collapse:first-of-type > .collapse__header.is-block:first-of-type) {
	background-color: $color-white;
	box-shadow:
		-1.5rem 0 0 $color-white,
		1.5rem 0 0 $color-white;
	padding: 0.6rem 0;
	border-radius: $border-radius-large $border-radius-large 0 0;
}

:deep(.collapse__button-label) {
	color: $color-primary;
	font-size: $font-large-4;
	font-weight: $font-weight-semibold;
	font-family: $font-family-special;
}

:deep(.price-slider__value) {
	margin-top: 0;
}

:deep(.price-slider__heading) {
	margin: 0;
}

:deep(.recommendation-rate__button.button) {
	padding: 0 0.9rem;
}

:deep(.checkbox--checked) {
	background: transparent;
	box-shadow: none;

	.checkbox__label {
		color: $color-primary;
		font-weight: $font-weight-semibold;
	}
}

:deep(.filter-tag-list) {
	margin-top: 0.4rem;
	margin-bottom: 0.2rem;
	border-color: transparent;
}

@include media-query-up($breakpoint-extralarge) {
	:deep(.filter-tag-list) {
		border-color: $color-primary-l4;
	}
}

:deep(.collapse__button-inline-text) {
	font-size: $font-medium-2;
	font-weight: $font-weight-regular;
}

.theme-landing {
	max-width: 144rem;
	position: relative;
	margin: 2rem auto;

	&-form__search-bar {
		&--mobile {
			margin-bottom: 0.8rem;
			margin-top: 0.8rem;
		}
	}

	.theme-landing-filter-section {
		background-color: $header-bar-bg;
		display: grid;
		grid-template-columns: repeat(3, 32rem);
		justify-content: center;
		align-items: stretch;
		gap: 2.6rem 7rem;
		padding: 1rem 10rem;
		border-left: 1px solid $color-primary-l4;
		border-right: 1px solid $color-primary-l4;
		border-bottom: 1px solid $color-primary-l4;
		padding-bottom: 2rem;
		padding-top: 3.2rem;
		border-radius: 0 0 $border-radius-large $border-radius-large;

		&__item {
			margin-bottom: 0.6rem;
		}

		&__filter-list {
			margin-top: -1rem;
			justify-self: center;
			grid-column: 1 / 4;
			margin-bottom: 0;
		}

		&__subheader {
			padding: 0;
			margin-top: 0;
			margin-bottom: 1.8rem;
			text-align: left;
			font-size: $font-medium-2;
			font-family: $font-family;
			font-weight: $font-weight-semibold;
			color: $color-text;
		}

		&__subheader--disabled {
			color: $color-state-disabled;
		}

		&__submit-button {
			height: 3.8rem;
			padding: 0.8rem 2.4rem;
			font-size: $font-small-1;
			line-height: 2.2rem;
			justify-self: center;
			grid-column: 1 / 4;
			margin-top: 3.2rem;
		}

		&__checkbox-group {
			background-color: $color-white;
			border: 1px solid $color-primary-l4;
			padding: 1rem;
			height: 36rem;
			border-radius: $border-radius-large;
			overflow: hidden;
		}

		&__line {
			grid-column: 1 / -1;
			height: 1px;
			border-bottom: 1px solid $color-primary-l4;
		}

		&__dropdown {
			margin-bottom: 2rem;
		}

		.recommendation-rate {
			justify-content: space-between;
		}
	}

	//STYLES MOBILE
	&-form {
		background: $header-bar-bg;
		display: flex;
		position: relative;
		flex-direction: column;
		align-items: center;
		max-width: 82rem;
		margin: 0 auto;
		padding: 0 1.5rem;
		border: 1px solid $color-lime-light;
		border-radius: $border-radius-large;

		.price-slider {
			margin-top: 1.7rem;
		}

		// form fields
		.theme-landing-form__travel-group,
		.theme-landing-form__transfer-group,
		.theme-landing-form__stay-group,
		.theme-landing-form__additional-group,
		.theme-landing-form__operator {
			display: flex;
			position: relative;
			flex-direction: column;
			justify-content: flex-end;
			width: 100%;
			margin-bottom: 0;
		}

		.theme-landing-form__transfer-group,
		.theme-landing-form__stay-group,
		.theme-landing-form__operator {
			border-top: 0.1rem solid $color-primary-l4;
		}

		.theme-landing-form__subheader {
			padding: 1.8rem 0 1.5rem;
			align-self: flex-start;
			font-family: $font-family;
			font-weight: $font-weight-semibold;
			color: $color-text;
			margin-top: 0;

			&--hotellist {
				// stylelint-disable-next-line
				@extend .theme-landing-form__subheader;

				color: $color-text;
				font-size: $font-medium-2;
				margin-bottom: 0;
			}
		}

		.theme-landing-form__field {
			width: 100%;
			margin-bottom: 0.5rem;
		}

		.theme-landing-form__transfer-group {
			// stylelint-disable-next-line
			@extend .theme-landing-form__field;
		}

		.theme-landing-form__room-type-field,
		.theme-landing-form__board-types-field {
			margin-bottom: 0.6rem;
		}

		.hotel-list-filters__skeleton {
			margin-bottom: 2.4rem;
		}

		.theme-landing-form__room-type-field,
		.theme-landing-form__board-types-field,
		.theme-landing-form__hotel-category-filter,
		.theme-landing-form__recommendation-filter,
		.theme-landing-form__price-filter,
		.theme-landing-form__collapse {
			width: 100%;
		}

		.theme-landing-form__operator-filter {
			margin-bottom: 2.4rem;

			// stylelint-disable-next-line
			@extend .theme-landing-form__field;

			padding-top: 2.4rem;
			border-top: 0.1rem solid $color-primary-l4;
		}

		.theme-landing-form__direct-flight-field {
			// stylelint-disable-next-line
			@extend .theme-landing-form__field;

			min-height: 4.1rem;
		}

		.theme-landing-form__transfer-field {
			// stylelint-disable-next-line
			@extend .theme-landing-form__field;

			margin-bottom: 1.9rem;
		}

		.theme-landing-form__passengers-field {
			margin-bottom: 3.2rem;
		}

		.theme-landing-form__sea-view-field {
			// stylelint-disable-next-line
			@extend .theme-landing-form__field;

			margin-top: 0.8rem;
			margin-bottom: 2.5rem;
		}

		.theme-landing-form__recommendation-filter {
			border-bottom: 0.1rem solid $color-primary-l4;
			padding-bottom: 2.4rem;
		}

		.theme-landing-form__filter-tag-list {
			padding-bottom: 1.9rem;
			padding-left: 0.7rem;
			padding-right: 0.7rem;
			margin-top: 1.4rem;

			:deep(.filter-tag-list-header) {
				.filter-tag-list-header__title {
					font-size: $font-medium-2 !important;
				}

				.link-button__text {
					font-size: $font-small-2 !important;

					@media (min-width: $breakpoint-small) {
						font-size: $font-small-1 !important;
					}
				}
			}
		}

		.hotel-list-filters__header--before-input {
			text-align: left;
			color: $color-text;
			font-size: $font-medium-2;
			font-weight: $font-weight-semibold;
			width: 100%;
			cursor: pointer;
			border-top: 0.1rem solid $color-primary-l4;
			margin-top: 1.8rem;
			padding: 1.8rem 0;
		}

		.hotel-list-filters__most-popular {
			margin-top: 3rem;

			:deep(.mostPopularFilters) {
				padding: 0 2rem;
				margin-bottom: 2rem;
				margin-top: 1rem;
			}

			:deep(.mostPopularFilters__item) {
				margin-bottom: 1.8rem;
			}
		}

		:deep(.additional-checkbox-filters) {
			padding: 0 2rem;
			margin-top: 1rem;
		}

		:deep(.additional-checkbox-filters__item) {
			margin-bottom: 1.8rem;
		}

		.collapse__header .hotel-list-filters__header--before-input {
			border-top: none;
			margin: 0;
			text-align: left;
			padding: 0 0 1.8rem;
			width: 100%;
			cursor: pointer;
			margin-top: 1.8rem;
			color: $color-primary;
		}

		.hotel-list-filters__collapse {
			position: relative;
			border-top: 0.1rem solid $color-primary-l4; // löschen?
		}

		.hotel-list-filters__header--destination {
			font-size: $font-medium-2;
			color: $color-text;
			border-top: none;
			padding-top: 0;
			margin-top: 1.6rem;
			padding-bottom: 0.6rem;
		}

		.hotel-list-filters__header--hotel-category {
			margin-top: 0;
		}

		.hotel-list-filters__header--operator {
			border-top: 0;
			margin-top: 0;
		}

		.theme-landing-form__footer {
			display: flex;
			justify-content: center;
			width: calc(100% + 0.1rem);
			margin: 2rem 0;
			position: sticky;
			bottom: 0;
			background-color: $header-bar-bg;
			border-top: 0.1rem solid $color-primary-l2;
			padding: 2.4rem 0;

			@include media-query-up($breakpoint-small) {
				padding: 1.5rem 0;
			}

			&::before {
				position: absolute;
				content: '';
				inset: 0 -1.4rem;
				background-color: inherit;
				z-index: -1;
			}
		}

		.theme-landing-form__submit-button {
			width: 100%;
			max-width: 28rem;
			height: 5.4rem;
			padding: 1rem 2rem;
		}

		//styles für desktop

		@include media-query-up($breakpoint-extralarge) {
			flex-direction: row;
			max-width: 144rem;
			padding: 2rem 1.5rem 1.5rem;
			border-radius: 0 $border-radius-small $border-radius-small;
			z-index: 3;

			.theme-landing-form__field {
				margin: 0 0.25rem;
			}

			.theme-landing-form__travel-group {
				min-width: 51rem;
			}

			.theme-landing-form__destination-field {
				position: absolute;
				z-index: 1;
				left: 0;
				width: 55%; // NOTE: this will be overwritten by js
				margin: 0;
			}

			.theme-landing-form__footer {
				width: auto;
				margin: 0 auto !important;
			}

			.theme-landing-form__submit-button {
				// IE width fix
				width: auto;
				min-width: 18rem;
				padding: 1.3rem;
				font-size: $font-medium-3;
				position: sticky;
			}
		}
	}
}

@include media-query-up($breakpoint-extralarge) {
	.theme-landing {
		padding-left: $padding-normal;
		padding-right: $padding-normal;

		.theme-landing-form {
			background-color: rgba($color-white, 1);
			border: 0.1rem solid $color-lime-light;
			box-shadow: 0 0.1rem 0 $color-lime-light;
			border-radius: $border-radius-large $border-radius-large 0 0;
			z-index: 99;
		}
	}
}

@include media-query-up($breakpoint-verylarge) {
	.theme-landing {
		padding-left: 0;
		padding-right: 0;
	}
}

.theme-landing-form--overflow-hidden {
	overflow: hidden;
}
</style>
